(() => {
  if (window.MOTPreorderOrderData) return;
  const script = document.currentScript;
  const endpoint = script?.dataset.preorderUrl;
  if (!endpoint) return;
  const root = window.Shopify?.routes?.root || '/';
  const nativeFetch = window.fetch.bind(window);
  const keys = ['_mot_preorder', '_mot_preorder_label', '_mot_preorder_message'];
  let running;
  let revision = 0;
  const noteSelector = '.upcart-public-notes__textarea, #CartPopup .NotesModule_NotesModule__ textarea, textarea[name="note"], input[name="note"]';
  let latestNote;
  let noteQueue = Promise.resolve();
  const noteXHRs = new Set();
  const rememberNote = event => {
    const field = event.composedPath().find(node => node.matches?.(noteSelector));
    if (field) latestNote = field.value;
  };
  document.addEventListener('input', rememberNote, true);
  document.addEventListener('change', rememberNote, true);
  const noteBody = body => {
    if (typeof body === 'string') {
      try {
        const value = JSON.parse(body);
        if (value && Object.hasOwn(value, 'note')) return {
          onlyNote: Object.keys(value).every(key => key === 'note'),
          replace: note => JSON.stringify({ ...value, note }),
          value: value.note,
        };
      } catch (_) {
        const value = new URLSearchParams(body);
        if (value.has('note')) return {
          onlyNote: [...value.keys()].every(key => key === 'note'),
          replace: note => { value.set('note', note); return value.toString(); },
          value: value.get('note'),
        };
      }
    }
    if (body instanceof URLSearchParams || body instanceof FormData) {
      if (!body.has('note')) return;
      return {
        onlyNote: [...body.keys()].every(key => key === 'note'),
        replace: note => {
          const value = body instanceof FormData ? new FormData() : new URLSearchParams();
          for (const [key, entry] of body.entries()) value.append(key, entry);
          value.set('note', note);
          return value;
        },
        value: body.get('note'),
      };
    }
  };
  const queueNote = task => {
    const result = noteQueue.then(task);
    noteQueue = result.catch(() => {});
    return result;
  };
  const noteTimeout = async task => {
    let timer;
    try {
      return await Promise.race([task, new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('Your order note could not be saved. Please try checkout again.')), 15000);
      })]);
    } finally { clearTimeout(timer); }
  };
  const snapshotNote = target => {
    if (latestNote !== undefined) return latestNote;
    const container = target.closest('#CartPopup') || target.form || target.closest('form');
    const field = container?.querySelector(noteSelector) ||
      [...document.querySelectorAll(noteSelector)].find(node => node.getClientRects().length > 0);
    if (field) latestNote = field.value;
    return latestNote;
  };
  const saveNote = async note => {
    if (note === undefined) return;
    latestNote = note;
    await noteTimeout(Promise.all([...noteXHRs]));
    let cancelled = false;
    let controller;
    try { await noteTimeout(queueNote(async () => {
      if (cancelled) throw new Error('Note save cancelled');
      controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await nativeFetch(root + 'cart/update.js', {
          method: 'POST', credentials: 'same-origin', cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }), signal: controller.signal,
        });
        if (!response.ok || ((await response.json()).note ?? '') !== note) throw new Error('Note save failed');
      } catch (_) {
        throw new Error('Your order note could not be saved. Please try checkout again.');
      } finally { clearTimeout(timer); }
    })); } finally { cancelled = true; controller?.abort(); }
  };
  const normalise = value => Array.isArray(value) ? Object.fromEntries(value) : { ...(value || {}) };
  const desiredProperties = (item, label) => {
    const properties = normalise(item.properties);
    if (item.preorder) {
      properties._mot_preorder = 'true';
      properties._mot_preorder_label = label || 'Pre-order';
      properties._mot_preorder_message = item.message || 'Contact us for the expected dispatch date';
    } else {
      keys.forEach(key => delete properties[key]);
    }
    return properties;
  };
  const same = (a, b) => Object.keys(a).length === Object.keys(b).length && Object.keys(a).every(key => a[key] === b[key]);
  const request = async (url, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await nativeFetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      if (!response.ok) throw new Error('Unable to save dispatch details. Please try checkout again.');
      return await response.text();
    } finally { clearTimeout(timer); }
  };
  const readStatus = async () => {
    const html = await request(endpoint);
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mot-upcart-preorder-data]');
    const data = JSON.parse(node?.textContent || 'null');
    if (!Array.isArray(data?.items) || data.items.some(item => !item.key || !Number.isInteger(item.quantity))) throw new Error('Dispatch details unavailable. Please try checkout again.');
    return data;
  };
  const synchronise = () => {
    if (running) return running;
    running = (async () => {
      for (let attempt = 0; attempt < 100; attempt++) {
        const currentRevision = revision;
        const data = await readStatus();
        const item = data.items.find(entry => !same(normalise(entry.properties), desiredProperties(entry, data.label)));
        if (!item) {
          if (currentRevision !== revision) continue;
          return;
        }
        const properties = desiredProperties(item, data.label);
        if (Object.keys(properties).length === 0) {
          if (item.isBundle) throw new Error('Please remove this item and add it again before checking out.');
          const replacement = { id: Number(item.variantId), quantity: item.quantity, properties };
          if (item.sellingPlanId) replacement.selling_plan = item.sellingPlanId;
          await request(root + 'cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          });
          try {
            await request(root + 'cart/add.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [replacement] }),
            });
          } catch (error) {
            await request(root + 'cart/add.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ ...replacement, properties: normalise(item.properties) }] }),
            });
            throw error;
          }
          continue;
        }
        await request(root + 'cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: item.quantity, properties }),
        });
      }
      throw new Error('Your cart changed while checking dispatch details. Please try checkout again.');
    })().finally(() => { running = null; });
    return running;
  };
  const formSnapshot = form => {
    const map = form.querySelector('[data-mot-preorder-order-map]');
    if (!map) return;
    const data = JSON.parse(map.textContent);
    const id = form.elements.namedItem('id')?.value;
    const entry = data.variants[String(id)];
    const quantity = Math.max(1, Number(form.elements.namedItem('quantity')?.value || 1));
    const preorder = !!entry?.available && (data.force || (entry.managed && entry.continue && quantity > Math.max(0, entry.stock)));
    const props = desiredProperties({ preorder, message: entry?.message, properties: {} }, data.label);
    for (const key of keys) {
      if (!(key in props)) form.querySelector(`input[name="properties[${key}]"]`)?.remove();
    }
    for (const [key, value] of Object.entries(props)) {
      let input = form.querySelector(`input[name="properties[${key}]"]`);
      if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = `properties[${key}]`; form.append(input); }
      input.value = value;
    }
  };
  document.addEventListener('change', event => { const form = event.target.form; if (form) formSnapshot(form); }, true);
  document.addEventListener('input', event => { if (event.target.name === 'quantity' && event.target.form) formSnapshot(event.target.form); }, true);
  window.addEventListener('submit', event => { if (event.target instanceof HTMLFormElement) formSnapshot(event.target); }, true);
  window.addEventListener('click', event => { const button = event.target.closest('button,input[type="submit"]'); if (button?.form) formSnapshot(button.form); }, true);
  document.addEventListener('formdata', event => {
    if (!event.target.querySelector('[data-mot-preorder-order-map]')) return;
    formSnapshot(event.target);
    keys.forEach(key => event.formData.delete(`properties[${key}]`));
    event.target.querySelectorAll('input[name^="properties[_mot_preorder"]').forEach(input => event.formData.set(input.name, input.value));
  }, true);
  const initialiseForms = () => document.querySelectorAll('form [data-mot-preorder-order-map]').forEach(node => formSnapshot(node.closest('form')));
  initialiseForms();
  document.addEventListener('DOMContentLoaded', initialiseForms);
  document.addEventListener('shopify:section:load', initialiseForms);
  document.addEventListener('theme:variant:change', event => {
    if (event.detail?.formEl) formSnapshot(event.detail.formEl);
  });
  const replay = new WeakSet();
  let checkoutBusy = false;
  const guard = async (event, target, resume) => {
    if (replay.has(target)) { replay.delete(target); return; }
    event.preventDefault(); event.stopImmediatePropagation();
    if (checkoutBusy) return;
    checkoutBusy = true;
    const note = snapshotNote(target);
    const cart = target.closest('#CartPopup') || target.form || target.closest('form');
    const previousInert = cart?.inert;
    if (cart) cart.inert = true;
    let error;
    try { await synchronise(); await saveNote(note); } catch (e) { error = e; }
    if (cart) cart.inert = previousInert;
    checkoutBusy = false;
    if (error) {
      let message = document.getElementById('mot-preorder-checkout-error');
      if (!message) { message = document.createElement('p'); message.id = 'mot-preorder-checkout-error'; message.setAttribute('role', 'alert'); target.after(message); }
      message.textContent = error.message;
      return;
    }
    document.getElementById('mot-preorder-checkout-error')?.remove();
    replay.add(target); resume();
  };
  window.addEventListener('click', event => {
    const target = event.target.closest('.upcart-checkout-button, button[name="checkout"], input[name="checkout"], a[href]');
    if (!target) return;
    if (target.matches('a[href]') && !target.matches('.upcart-checkout-button')) {
      const url = new URL(target.href, location.href);
      if (url.origin !== location.origin || !/\/(checkout|checkouts|cart\/c)(\/|$)/.test(url.pathname)) return;
    }
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    guard(event, target, () => target.click());
  }, true);
  window.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || event.submitter?.name !== 'checkout') return;
    const submitter = event.submitter;
    guard(event, form, () => form.requestSubmit(submitter));
  }, true);
  let refreshTimer;
  const refreshCart = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      if (typeof window.upcartRefreshCart === 'function') {
        Promise.resolve(window.upcartRefreshCart()).catch(() => {});
      }
    }, 0);
  };
  window.fetch = async (...args) => {
    const url = new URL(typeof args[0] === 'string' || args[0] instanceof URL ? args[0] : args[0].url, location.href);
    const method = String(args[1]?.method || args[0]?.method || 'GET').toUpperCase();
    let note;
    if (method === 'POST' && url.origin === location.origin && /\/cart\/update(\.js)?$/.test(url.pathname)) {
      const body = args[1]?.body ?? (args[0] instanceof Request ? await args[0].clone().text() : undefined);
      note = noteBody(body);
    }
    const send = () => {
      if (!note) return nativeFetch(...args);
      const body = note.replace(latestNote ?? note.value);
      return nativeFetch(args[0], { ...args[1], body });
    };
    const response = await (note ? queueNote(send) : send());
    if (note?.onlyNote) return response;
    if (response.ok && method === 'POST' && url.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname)) {
      revision++;
      try { await synchronise(); refreshCart(); } catch (_) {}
    }
    return response;
  };
  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  const xhrMutations = new WeakSet();
  const xhrUpdates = new WeakSet();
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    const target = new URL(url, location.href);
    if (String(method).toUpperCase() === 'POST' && target.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(target.pathname)) xhrMutations.add(this);
    else xhrMutations.delete(this);
    if (String(method).toUpperCase() === 'POST' && target.origin === location.origin && /\/cart\/update(\.js)?$/.test(target.pathname)) xhrUpdates.add(this);
    else xhrUpdates.delete(this);
    return xhrOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    const note = xhrUpdates.has(this) ? noteBody(args[0]) : undefined;
    let complete;
    if (note) {
      args[0] = note.replace(latestNote ?? note.value);
      const pending = new Promise(resolve => { complete = resolve; });
      noteXHRs.add(pending);
      this.addEventListener('loadend', () => { noteXHRs.delete(pending); complete(); }, { once: true });
    }
    if (xhrMutations.has(this) && !note?.onlyNote) this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) {
        revision++;
        synchronise().then(refreshCart).catch(() => {});
      }
    }, { once: true });
    try { return xhrSend.apply(this, args); } catch (error) { complete?.(); throw error; }
  };
  document.addEventListener('baseline:cart:afteradditem', () => {
    revision++;
    synchronise().then(refreshCart).catch(() => {});
  });
  window.addEventListener('pageshow', () => synchronise().then(refreshCart).catch(() => {}));
  window.MOTPreorderOrderData = { synchronise, desiredProperties, formSnapshot };
})();
